import { motion } from 'framer-motion';
import { Check, Users, Target, Heart, Truck, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

// Prose propre à la page : elle vit à côté de sa mise en page, comme dans
// ChatWidget. Le dictionnaire de LanguageContext garde les libellés partagés.
const TEXTES = {
  fr: {
    seoTitre: "À propos d'AfriOne BD - Votre épicerie africaine en Belgique",
    seoDescription: "AfriOne BD rend les produits africains accessibles à tous en Belgique : qualité, prix compétitifs et livraison rapide depuis Liège. Découvrez notre mission.",
    surtitre: 'À propos',
    accroche1: 'Bienvenue chez ',
    accroche2: ", votre solution simple et rapide pour accéder à l'alimentation africaine en Belgique 🇧🇪",
    missionTitre: 'Rendre les produits africains accessibles à tous',
    missionTexte: 'Notre mission est de rendre les produits africains accessibles à tous, avec des prix très compétitifs, une excellente qualité et une livraison rapide partout en Belgique.',
    etiquetteQualite: 'Qualité',
    etiquettePrix: 'Prix compétitifs',
    etiquetteLivraison: 'Livraison rapide',
    altEquipe: 'Famille africaine partageant un repas',
    constatTitre: "Aujourd'hui, accéder à l'alimentation africaine en Europe est souvent compliqué",
    constatConclusion: 'Tout cela rend les courses longues, fatigantes et contraignantes.',
    solutionTitre: 'Chez AfriOne BD, nous avons décidé de simplifier votre quotidien',
    solutionConclusion: 'Vous pouvez désormais faire vos courses sans stress, sans déplacement et sans perte de temps.',
    but1: "Permettre à tous les Africains en Belgique d'avoir facilement accès aux produits de chez nous",
    but2: 'Offrir des prix accessibles à toutes les familles',
    but3: 'Livrer rapidement, où que vous soyez en Belgique',
    promesseIntro: "Avec AfriOne BD, vous n'avez plus besoin de :",
    promesse1: '❌ chercher une place de parking',
    promesse2: '❌ faire la file en magasin',
    promesse3: '❌ perdre du temps dans les déplacements',
    promesseChute: '👉 Vous commandez, nous livrons.',
    merci: 'Merci pour votre confiance',
  },
  en: {
    seoTitre: 'About AfriOne BD - Your African grocery store in Belgium',
    seoDescription: 'AfriOne BD makes African products available to everyone in Belgium: quality, competitive prices and fast delivery from Liège. Discover our mission.',
    surtitre: 'About',
    accroche1: 'Welcome to ',
    accroche2: ', your simple and fast way to get African food in Belgium 🇧🇪',
    missionTitre: 'Making African products available to everyone',
    missionTexte: 'Our mission is to make African products available to everyone, with very competitive prices, excellent quality and fast delivery throughout Belgium.',
    etiquetteQualite: 'Quality',
    etiquettePrix: 'Competitive prices',
    etiquetteLivraison: 'Fast delivery',
    altEquipe: 'African family sharing a meal',
    constatTitre: 'Today, getting African food in Europe is often complicated',
    constatConclusion: 'All of this makes shopping long, tiring and inconvenient.',
    solutionTitre: 'At AfriOne BD, we decided to make your daily life simpler',
    solutionConclusion: 'You can now do your shopping without stress, without travelling and without wasting time.',
    but1: 'Give every African in Belgium easy access to the products from back home',
    but2: 'Offer prices every family can afford',
    but3: 'Deliver quickly, wherever you are in Belgium',
    promesseIntro: 'With AfriOne BD, you no longer need to:',
    promesse1: '❌ look for a parking space',
    promesse2: '❌ queue in the shop',
    promesse3: '❌ lose time travelling',
    promesseChute: '👉 You order, we deliver.',
    merci: 'Thank you for your trust',
  },
};

export default function About() {
  const { t, language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  
  const solutions = [
    { text: 'des produits africains de qualité', textEn: 'quality African products' },
    { text: 'des prix très compétitifs', textEn: 'very competitive prices' },
    { text: 'un excellent rapport qualité-prix', textEn: 'excellent value for money' },
    { text: 'une livraison rapide partout en Belgique', textEn: 'fast delivery throughout Belgium' },
  ];
  
  const problems = [
    { text: 'Les produits sont parfois très chers', textEn: 'Products are sometimes very expensive' },
    { text: 'Les magasins sont souvent situés en centre-ville', textEn: 'Stores are often located in city centers' },
    { text: 'Il est difficile de se garer ou de se déplacer', textEn: 'It\'s difficult to park or travel' },
    { text: 'Après le travail, la fatigue rend les courses compliquées', textEn: 'After work, fatigue makes shopping complicated' },
    { text: 'Les parents et les familles manquent de temps', textEn: 'Parents and families lack time' },
  ];
  
  const commitments = [
    { icon: Clock, text: 'vous faire gagner du temps', textEn: 'save you time' },
    { icon: Package, text: 'vous offrir des produits de qualité', textEn: 'offer you quality products' },
    { icon: Truck, text: 'rendre vos courses simples et rapides', textEn: 'make your shopping simple and fast' },
    { icon: Heart, text: 'vous proposer les meilleurs prix possibles', textEn: 'offer you the best possible prices' },
  ];
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 overflow-x-hidden" data-testid="about-page">
      <SEO
        title={x.seoTitre}
        description={x.seoDescription}
        canonical="https://afrionebd.eu/about"
      />
      {/* Hero */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-br from-primary/5 via-secondary/10 to-accent/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{x.surtitre}</span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="about-title">
              {t('aboutTitle')}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-text_secondary mt-5 leading-relaxed">
              {x.accroche1}<span className="text-primary font-semibold">AfriOne BD</span>{x.accroche2}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Mission */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourMission')}</span>
              <h2 className="text-xl sm:text-2xl lg:text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-text_primary mt-2 tracking-tight mb-4 sm:mb-6">
                {x.missionTitre}
              </h2>
              <p className="text-text_secondary leading-relaxed mb-6">
                {x.missionTexte}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary/50 rounded-full">
                  <Check className="w-5 h-5 text-secondary_hover" />
                  <span className="text-sm font-medium text-text_primary">{x.etiquetteQualite}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                  <Check className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-text_primary">{x.etiquettePrix}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-accent/20 rounded-full">
                  <Check className="w-5 h-5 text-accent" />
                  <span className="text-sm font-medium text-text_primary">{x.etiquetteLivraison}</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/images/a-propos-equipe.jpg"
                alt={x.altEquipe}
                className="w-full rounded-3xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Observation */}
      <section className="py-16 lg:py-24 bg-text_primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourObservation')}</span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading mt-2 tracking-tight">
              {x.constatTitre}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white/5 rounded-2xl border border-white/10"
              >
                <span className="text-lg sm:text-2xl font-bold text-primary/50">{index + 1}</span>
                <p className="text-white/80 mt-2">
                  {language === 'fr' ? problem.text : problem.textEn}
                </p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-white/60 mt-6 sm:mt-8 text-sm sm:text-base">
            {x.constatConclusion}
          </p>
        </div>
      </section>
      
      {/* Our Solution */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{t('ourSolution')}</span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-text_primary mt-2 tracking-tight">
              {x.solutionTitre}
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 p-5 bg-secondary/30 rounded-2xl"
              >
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-secondary_hover" />
                </div>
                <p className="font-medium text-text_primary">
                  {language === 'fr' ? solution.text : solution.textEn}
                </p>
              </motion.div>
            ))}
          </div>
          
          <p className="text-center text-text_secondary mt-8">
            {x.solutionConclusion}
          </p>
        </div>
      </section>
      
      {/* Goals & Commitments */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-secondary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Goal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold font-heading text-text_primary mb-3 sm:mb-4">{t('ourGoal')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">{x.but1}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">{x.but2}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">→</span>
                  <span className="text-text_secondary">{x.but3}</span>
                </li>
              </ul>
            </motion.div>
            
            {/* Commitment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-3xl shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-secondary_hover" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold font-heading text-text_primary mb-3 sm:mb-4">{t('ourCommitment')}</h3>
              <ul className="space-y-3">
                {commitments.map((commitment, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center flex-shrink-0">
                      <commitment.icon className="w-4 h-4 text-secondary_hover" />
                    </div>
                    <span className="text-text_secondary">
                      {language === 'fr' ? commitment.text : commitment.textEn}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Promise */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary to-primary_hover p-8 lg:p-12 rounded-3xl text-white text-center"
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading mb-4 sm:mb-6">{t('ourPromise')}</h2>
            <p className="text-sm sm:text-base lg:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
              {x.promesseIntro}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">{x.promesse1}</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">{x.promesse2}</span>
              <span className="px-4 py-2 bg-white/10 rounded-full text-sm">{x.promesse3}</span>
            </div>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold">
              {x.promesseChute}
            </p>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/80">{x.merci}</p>
              <p className="text-base sm:text-lg font-bold mt-2">AfriOne BD</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
